import {
  CheckIcon,
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
  Trash2Icon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { findMatchingSku } from '@/components/CashRegister/VariantAxisPicker'
import { StatusBadge } from '@/components/ds'
import { PickupPointMap } from '@/components/PickupPointMap/PickupPointMap'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Collection } from '@/interfaces/Collection'
import {
  Cart,
  CartItem,
  CreatedCart,
  DeliveryMethod,
  DeliveryMethodType,
  EshopMoney,
  EshopPaymentMethod,
  PaymentMethodType,
  PromotedOrder,
  StorefrontCategory,
  StorefrontProduct,
  StorefrontSku,
} from '@/interfaces/eshop'
import {
  createCarrierPickupSource,
  createPersonalPickupSource,
} from '@/lib/eshop/pickupPointMap'
import { lineTotalInclVat, priceInclVat } from '@/lib/eshop/prices'
import { StorefrontClient } from '@/lib/eshop/storefrontApi'
import { formatCurrency } from '@/lib/formats/formatCurrency'

type Step = 'catalog' | 'cart' | 'delivery' | 'payment' | 'review'

const steps: Step[] = ['catalog', 'cart', 'delivery', 'payment', 'review']

const carrierPickupTypes: DeliveryMethodType[] = [
  'balikovna',
  'ppl',
  'zasilkovna',
]

const formatMoney = (money: EshopMoney) =>
  formatCurrency(parseInt(money.amount, 10) / 100, money.currency)

const cartItemUnitPrice = (item: CartItem): EshopMoney | null =>
  item.unitPriceInclVat ??
  (item.unitPrice ? priceInclVat(item.unitPrice, item.taxRate) : null)

const cartItemLineTotal = (item: CartItem): EshopMoney | null => {
  if (!item.unitPrice) return null

  return lineTotalInclVat(item.unitPrice, item.quantity, item.taxRate)
}

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : undefined

const NO_EXTRA = 'none'

const formatLineExtras = (item: CartItem) =>
  item.lineExtras
    ?.map((extra) => `${extra.parameterName}: ${extra.valueLabel}`)
    .join(', ')

type CustomerForm = {
  name: string
  email: string
  phone: string
  street: string
  city: string
  zip: string
  country: string
}

type StoredCart = {
  id: string
  secretToken: string
}

const CART_STORAGE_KEY = 'eshop.storefront-tester.cart'

const readStoredCart = (): StoredCart | null => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredCart>
    if (
      typeof parsed.id === 'string' &&
      typeof parsed.secretToken === 'string'
    ) {
      return { id: parsed.id, secretToken: parsed.secretToken }
    }
  } catch {
    // ignore invalid storage
  }

  return null
}

const writeStoredCart = (stored: StoredCart) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(stored))
}

const clearStoredCart = () => {
  localStorage.removeItem(CART_STORAGE_KEY)
}

export const StorefrontTester = ({ client }: { client: StorefrontClient }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.tester',
  })

  const [step, setStep] = useState<Step>('catalog')
  const [busy, setBusy] = useState(false)

  const [categories, setCategories] = useState<StorefrontCategory[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all')
  const [products, setProducts] = useState<StorefrontProduct[]>([])
  const [skus, setSkus] = useState<StorefrontSku[]>([])
  // Selected surcharge value per product and parameter
  const [extras, setExtras] = useState<
    Record<string, Record<string, string>>
  >({})
  // Selected axis value per product and axis
  const [variants, setVariants] = useState<
    Record<string, Record<string, string>>
  >({})

  const [cart, setCart] = useState<Cart | null>(null)
  const [cartToken, setCartToken] = useState<string | null>(
    () => readStoredCart()?.secretToken ?? null,
  )

  const [customer, setCustomer] = useState<CustomerForm>({
    name: 'John Doe',
    email: 'c1@paveljurasek.cz',
    phone: '731001840',
    street: 'Náměstí Interbrigády 6',
    city: 'Praha',
    zip: '602 00',
    country: 'CZ',
  })

  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethod[]>([])
  const [selectedDeliveryType, setSelectedDeliveryType] =
    useState<DeliveryMethodType | null>(null)
  const [selectedPickupPointId, setSelectedPickupPointId] = useState<
    string | null
  >(null)

  const [paymentMethods, setPaymentMethods] = useState<EshopPaymentMethod[]>(
    [],
  )

  const [order, setOrder] = useState<PromotedOrder | null>(null)

  useEffect(() => {
    let ignore = false

    Promise.all([
      client.get<Collection<StorefrontCategory>>('/categories'),
      client.get<Collection<StorefrontProduct>>('/products'),
      client.get<Collection<StorefrontSku>>('/skus'),
    ])
      .then(([categoriesResponse, productsResponse, skusResponse]) => {
        if (ignore) return
        setCategories(categoriesResponse.data)
        setProducts(productsResponse.data)
        setSkus(skusResponse.data)
      })
      .catch((error: unknown) => {
        if (ignore) return
        toast.error(t('errors.load'), { description: errorMessage(error) })
      })

    return () => {
      ignore = true
    }
  }, [client, t])

  useEffect(() => {
    const stored = readStoredCart()
    if (!stored) return

    let ignore = false

    client
      .get<Cart>(`/carts/${stored.id}`, { cartToken: stored.secretToken })
      .then((restored) => {
        if (ignore) return

        if (restored.status === 'promoted' || restored.promotedOrderId) {
          clearStoredCart()
          setCartToken(null)
          setCart(null)
          return
        }

        setCartToken(stored.secretToken)
        setCart(restored)
      })
      .catch(() => {
        if (ignore) return
        clearStoredCart()
        setCartToken(null)
        setCart(null)
      })

    return () => {
      ignore = true
    }
  }, [client])

  const cartItems = cart?.items.filter((item) => item.type === 'product') ?? []
  const deliveryItem =
    cart?.items.find((item) => item.type === 'delivery') ?? null
  const paymentItem =
    cart?.items.find((item) => item.type === 'payment') ?? null

  const run = async <T,>(
    action: () => Promise<T>,
    errorKey: string,
  ): Promise<T | null> => {
    setBusy(true)
    try {
      return await action()
    } catch (error) {
      toast.error(t(errorKey), { description: errorMessage(error) })
      return null
    } finally {
      setBusy(false)
    }
  }

  const selectCategory = async (categoryId: string) => {
    setSelectedCategoryId(categoryId)
    const query = categoryId === 'all' ? '' : `?mainCategoryId=${categoryId}`
    const response = await run(
      () => client.get<Collection<StorefrontProduct>>(`/products${query}`),
      'errors.load',
    )
    if (response) setProducts(response.data)
  }

  const addToCart = async (sku: StorefrontSku) => {
    await run(async () => {
      let cartId = cart?.id
      let token = cartToken

      if (!cartId || !token) {
        const created = await client.post<CreatedCart>('/carts', {})
        cartId = created.id
        token = created.secretToken
        writeStoredCart({ id: created.id, secretToken: created.secretToken })
        setCartToken(created.secretToken)
      }

      const updated = await client.post<Cart>(
        `/carts/${cartId}/items`,
        {
          skuId: sku.id,
          quantity: 1,
          extras: Object.values(extras[sku.productId] ?? {})
            .filter((valueId) => valueId !== NO_EXTRA)
            .map((parameterValueId) => ({ parameterValueId })),
        },
        { cartToken: token },
      )
      setCart(updated)
      toast.success(t('catalog.addedToCart', { name: sku.name }))
    }, 'errors.addToCart')
  }

  const selectExtra = (productId: string, parameterId: string, valueId: string) =>
    setExtras((current) => ({
      ...current,
      [productId]: { ...current[productId], [parameterId]: valueId },
    }))

  const selectVariant = (productId: string, axisId: string, valueId: string) =>
    setVariants((current) => ({
      ...current,
      [productId]: { ...current[productId], [axisId]: valueId },
    }))

  const updateQuantity = async (itemId: string, quantity: number) => {
    if (!cart || !cartToken) return

    await run(async () => {
      const updated =
        quantity <= 0
          ? await client.del<Cart>(`/carts/${cart.id}/items/${itemId}`, {
              cartToken,
            })
          : await client.patch<Cart>(
              `/carts/${cart.id}/items/${itemId}`,
              { quantity },
              { cartToken },
            )
      setCart(updated)
    }, 'errors.updateCart')
  }

  const removeItem = async (itemId: string) => {
    if (!cart || !cartToken) return

    await run(async () => {
      const updated = await client.del<Cart>(
        `/carts/${cart.id}/items/${itemId}`,
        { cartToken },
      )
      setCart(updated)
    }, 'errors.updateCart')
  }

  const enterDelivery = async () => {
    if (!cart) return

    const response = await run(
      () =>
        client.get<Collection<DeliveryMethod>>(
          `/delivery-methods?cartId=${cart.id}`,
        ),
      'errors.load',
    )

    if (response) {
      setDeliveryMethods(response.data)
      setStep('delivery')
    }
  }

  const saveCustomer = async () => {
    if (!cart || !cartToken) return

    await run(async () => {
      const updated = await client.patch<Cart>(
        `/carts/${cart.id}/customer`,
        {
          name: customer.name || null,
          email: customer.email || null,
          phone: customer.phone || null,
          billingStreet: customer.street || null,
          billingCity: customer.city || null,
          billingZip: customer.zip || null,
          billingCountry: customer.country || null,
          shippingStreet: customer.street || null,
          shippingCity: customer.city || null,
          shippingZip: customer.zip || null,
          shippingCountry: customer.country || null,
        },
        { cartToken },
      )
      setCart(updated)
      toast.success(t('delivery.customerSaved'))
    }, 'errors.saveCustomer')
  }

  const selectDeliveryType = (type: DeliveryMethodType) => {
    setSelectedDeliveryType(type)
    setSelectedPickupPointId(null)
  }

  const applyDelivery = async () => {
    if (!cart || !cartToken || !selectedDeliveryType) return

    await run(async () => {
      const updated = await client.patch<Cart>(
        `/carts/${cart.id}/delivery`,
        {
          type: selectedDeliveryType,
          ...(selectedDeliveryType === 'personalPickup'
            ? { pickupPointId: selectedPickupPointId }
            : carrierPickupTypes.includes(selectedDeliveryType)
              ? { carrierPickupPointId: selectedPickupPointId }
              : {}),
        },
        { cartToken },
      )
      setCart(updated)
      toast.success(t('delivery.deliverySaved'))
    }, 'errors.saveDelivery')
  }

  const enterPayment = async () => {
    if (!cart) return

    const response = await run(
      () =>
        client.get<Collection<EshopPaymentMethod>>(
          `/payment-methods?cartId=${cart.id}`,
        ),
      'errors.load',
    )

    if (response) {
      setPaymentMethods(response.data)
      setStep('payment')
    }
  }

  const applyPayment = async (type: PaymentMethodType) => {
    if (!cart || !cartToken) return

    await run(async () => {
      const updated = await client.patch<Cart>(
        `/carts/${cart.id}/payment`,
        { type },
        { cartToken },
      )
      setCart(updated)
      toast.success(t('payment.paymentSaved'))
    }, 'errors.savePayment')
  }

  const promote = async () => {
    if (!cart || !cartToken) return

    await run(async () => {
      const created = await client.post<PromotedOrder>(
        `/carts/${cart.id}/promotion`,
        {},
        { cartToken },
      )
      clearStoredCart()
      setCart(null)
      setCartToken(null)
      setOrder(created)
      toast.success(t('review.orderCreated'))
    }, 'errors.promote')
  }

  const selectedDeliveryMethod = deliveryMethods.find(
    (method) => method.type === selectedDeliveryType,
  )
  const usesCarrierWidget =
    (selectedDeliveryType === 'zasilkovna' &&
      selectedDeliveryMethod?.zasilkovnaPickupSelector === 'widget') ||
    (selectedDeliveryType === 'balikovna' &&
      selectedDeliveryMethod?.balikovnaPickupSelector === 'widget')
  const usesZasilkovnaWidget =
    selectedDeliveryType === 'zasilkovna' &&
    selectedDeliveryMethod?.zasilkovnaPickupSelector === 'widget'
  const usesBalikovnaWidget =
    selectedDeliveryType === 'balikovna' &&
    selectedDeliveryMethod?.balikovnaPickupSelector === 'widget'

  const needsPickupPoint =
    selectedDeliveryType === 'personalPickup' ||
    (selectedDeliveryType !== null &&
      carrierPickupTypes.includes(selectedDeliveryType) &&
      !usesCarrierWidget)

  const pickupPointSource =
    selectedDeliveryType === 'personalPickup'
      ? createPersonalPickupSource(client)
      : selectedDeliveryType !== null &&
          carrierPickupTypes.includes(selectedDeliveryType) &&
          !usesCarrierWidget
        ? createCarrierPickupSource(client, selectedDeliveryType)
        : null

  const customerComplete = cart?.customer.email && cart?.customer.name

  const skusForProduct = (productId: string) =>
    skus.filter((sku) => sku.productId === productId && !sku.isSystem)

  const productAxes = (product: StorefrontProduct) =>
    [...(product.variantAxes ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)

  const selectedSku = (product: StorefrontProduct) => {
    const axes = productAxes(product)
    const valueIds = axes
      .map((axis) => variants[product.id]?.[axis.axisId])
      .filter((id): id is string => !!id)

    return valueIds.length === axes.length
      ? findMatchingSku(skusForProduct(product.id), valueIds)
      : undefined
  }

  // A value is available when some SKU matches it together with the other selected values
  const isVariantValueAvailable = (
    product: StorefrontProduct,
    axisId: string,
    valueId: string,
  ) => {
    const valueIds = Object.values({
      ...variants[product.id],
      [axisId]: valueId,
    })

    return skusForProduct(product.id).some((sku) =>
      valueIds.every((id) => sku.optionValueIds?.includes(id)),
    )
  }

  const skuRow = (sku: StorefrontSku) => (
    <div key={sku.id} className="flex items-center justify-between gap-2">
      <div className="flex flex-col">
        <span className="text-sm">{sku.name}</span>
        <span className="text-sm font-medium">
          {sku.price ? formatMoney(sku.price.price) : t('catalog.noPrice')}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={busy || !sku.price}
        onClick={() => addToCart(sku)}
      >
        <PlusIcon />
        {t('catalog.addToCart')}
      </Button>
    </div>
  )

  const stepReachable = (target: Step): boolean => {
    switch (target) {
      case 'catalog':
        return true
      case 'cart':
        return cart !== null
      case 'delivery':
        return cartItems.length > 0
      case 'payment':
        return deliveryItem !== null
      case 'review':
        return paymentItem !== null
    }
  }

  const goToStep = (target: Step) => {
    if (target === 'delivery') {
      enterDelivery()
    } else if (target === 'payment') {
      enterPayment()
    } else {
      setStep(target)
    }
  }

  const cartSummary = cart && (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('totals.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">
            {t('totals.productsSubtotalExclVat')}
          </span>
          <span>{formatMoney(cart.totals.productsSubtotalExclVat)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">
            {t('totals.productsSubtotalInclVat')}
          </span>
          <span>{formatMoney(cart.totals.productsSubtotalInclVat)}</span>
        </div>
        {cart.totals.deliveryFee && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t('totals.deliveryFee')}
            </span>
            <span>{formatMoney(cart.totals.deliveryFee)}</span>
          </div>
        )}
        {cart.totals.paymentFee && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t('totals.paymentFee')}
            </span>
            <span>{formatMoney(cart.totals.paymentFee)}</span>
          </div>
        )}
        <Separator className="my-1" />
        <div className="flex justify-between font-medium">
          <span>{t('totals.totalInclVat')}</span>
          <span>{formatMoney(cart.totals.totalInclVat)}</span>
        </div>
      </CardContent>
    </Card>
  )

  if (order) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <CheckIcon className="size-12 rounded-full bg-success-bg p-2 text-success" />
        <h2 className="text-xl font-medium tracking-[-0.01em]">{t('review.orderCreated')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('review.orderSymbol')}: <span className="font-mono">{order.symbol}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          {t('review.orderUuid')}: <span className="font-mono">{order.orderUuid}</span>
        </p>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/orders">{t('review.goToOrders')}</Link>
          </Button>
          <Button
            onClick={() => {
              clearStoredCart()
              setOrder(null)
              setCart(null)
              setCartToken(null)
              setSelectedDeliveryType(null)
              setSelectedPickupPointId(null)
              setStep('catalog')
            }}
          >
            {t('review.startOver')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        {steps.map((item, index) => (
          <Button
            key={item}
            variant={item === step ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            disabled={!stepReachable(item) || busy}
            onClick={() => goToStep(item)}
          >
            <span className="text-xs opacity-70 tabular">{index + 1}.</span>
            {t(`steps.${item}`)}
          </Button>
        ))}
        <div className="ml-auto">
          {cart && (
            <span className="inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[13px] tabular">
              <ShoppingCartIcon className="size-3.5 text-muted-foreground" />
              {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
              <span className="text-muted-foreground">ks,</span>
              {formatMoney(cart.totals.totalInclVat)}
            </span>
          )}
        </div>
      </div>

      {step === 'catalog' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Label>{t('catalog.category')}</Label>
            <Select
              value={selectedCategoryId}
              onValueChange={selectCategory}
              disabled={busy}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('catalog.allCategories')}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.materializedName || category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {products.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t('catalog.empty')}
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {products.map((product) => (
              <Card key={product.id}>
                <CardHeader>
                  <CardTitle className="text-base">{product.name}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {product.parameters
                    .filter(
                      ({ parameter }) =>
                        parameter.isSurcharge && parameter.values.length > 0,
                    )
                    .map(({ parameter }) => (
                      <div key={parameter.id} className="flex flex-col gap-1">
                        <Label>{parameter.name}</Label>
                        <Select
                          value={extras[product.id]?.[parameter.id] ?? NO_EXTRA}
                          onValueChange={(valueId) =>
                            selectExtra(product.id, parameter.id, valueId)
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_EXTRA}>
                              {t('catalog.extraNone')}
                            </SelectItem>
                            {parameter.values.map((value) => (
                              <SelectItem key={value.id} value={value.id}>
                                {value.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  {skusForProduct(product.id).length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t('catalog.noSkus')}
                    </p>
                  )}
                  {productAxes(product).length === 0 &&
                    skusForProduct(product.id).map(skuRow)}
                  {productAxes(product).length > 0 &&
                    skusForProduct(product.id).length > 0 && (
                      <>
                        {productAxes(product).map((assignment) => (
                          <div
                            key={assignment.axisId}
                            className="flex flex-col gap-1"
                          >
                            <Label>{assignment.axis.name}</Label>
                            <Select
                              value={
                                variants[product.id]?.[assignment.axisId] ?? ''
                              }
                              onValueChange={(valueId) =>
                                selectVariant(
                                  product.id,
                                  assignment.axisId,
                                  valueId,
                                )
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={t('catalog.selectVariant')}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {(assignment.axis.values ?? []).map((value) => (
                                  <SelectItem
                                    key={value.id}
                                    value={value.id}
                                    disabled={
                                      !isVariantValueAvailable(
                                        product,
                                        assignment.axisId,
                                        value.id,
                                      )
                                    }
                                  >
                                    {value.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                        {selectedSku(product)
                          ? skuRow(selectedSku(product)!)
                          : Object.keys(variants[product.id] ?? {}).length ===
                              productAxes(product).length && (
                              <p className="text-sm text-muted-foreground">
                                {t('catalog.variantUnavailable')}
                              </p>
                            )}
                      </>
                    )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {step === 'cart' && cart && (
        <div className="flex flex-col gap-4">
          {cartItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('cart.empty')}</p>
          ) : (
            <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('cart.item')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('cart.unitPrice')}</TableHead>
                  <TableHead>{t('cart.quantity')}</TableHead>
                  <TableHead className="text-right">
                    {t('cart.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cartItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="w-full max-w-0 md:w-auto md:max-w-none">
                      <span className="block truncate md:inline">{item.name}</span>
                      {item.lineExtras && item.lineExtras.length > 0 && (
                        <span className="block truncate text-xs text-muted-foreground">{formatLineExtras(item)}</span>
                      )}
                      <p className="truncate text-xs text-muted-foreground md:hidden">
                        {cartItemUnitPrice(item) ? formatMoney(cartItemUnitPrice(item)!) : '-'}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {cartItemUnitPrice(item)
                        ? formatMoney(cartItemUnitPrice(item)!)
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                        >
                          <MinusIcon />
                        </Button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                        >
                          <PlusIcon />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-danger"
                        disabled={busy}
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2Icon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}

          {cartSummary}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('catalog')}>
              {t('back')}
            </Button>
            <Button
              disabled={busy || cartItems.length === 0}
              onClick={enterDelivery}
            >
              {t('continue')}
            </Button>
          </div>
        </div>
      )}

      {step === 'delivery' && cart && (
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('delivery.customerTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.name')}</Label>
                  <Input
                    value={customer.name}
                    onChange={(event) =>
                      setCustomer({ ...customer, name: event.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.email')}</Label>
                  <Input
                    type="email"
                    value={customer.email}
                    onChange={(event) =>
                      setCustomer({ ...customer, email: event.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.phone')}</Label>
                  <Input
                    value={customer.phone}
                    onChange={(event) =>
                      setCustomer({ ...customer, phone: event.target.value })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.street')}</Label>
                  <Input
                    value={customer.street}
                    onChange={(event) =>
                      setCustomer({ ...customer, street: event.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.city')}</Label>
                  <Input
                    value={customer.city}
                    onChange={(event) =>
                      setCustomer({ ...customer, city: event.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.zip')}</Label>
                  <Input
                    value={customer.zip}
                    onChange={(event) =>
                      setCustomer({ ...customer, zip: event.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('delivery.fields.country')}</Label>
                  <Input
                    value={customer.country}
                    onChange={(event) =>
                      setCustomer({ ...customer, country: event.target.value })
                    }
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  disabled={busy || !customer.name || !customer.email}
                  onClick={saveCustomer}
                >
                  {t('delivery.saveCustomer')}
                </Button>
                {customerComplete && (
                  <StatusBadge status="paid" label={t('delivery.customerComplete')} />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('delivery.deliveryTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {deliveryMethods.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('delivery.noMethods')}
                </p>
              )}
              <div className="flex flex-col gap-2">
                {deliveryMethods.map((method) => {
                  const price = method.prices.find(
                    (item) => item.price.currency === cart.currency,
                  )

                  return (
                    <label
                      key={method.type}
                      className="flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted has-[:checked]:border-foreground"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="deliveryType"
                          checked={selectedDeliveryType === method.type}
                          onChange={() => selectDeliveryType(method.type)}
                        />
                        <span className="text-sm font-medium">
                          {t(`deliveryTypes.${method.type}`)}
                        </span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {price ? formatMoney(price.price) : null}
                      </span>
                    </label>
                  )
                })}
              </div>

              {needsPickupPoint && pickupPointSource && (
                <div className="flex flex-col gap-2">
                  <Label>{t('delivery.pickupPoint')}</Label>
                  <PickupPointMap
                    key={selectedDeliveryType ?? 'none'}
                    source={pickupPointSource}
                    customerAddress={{
                      street: customer.street,
                      city: customer.city,
                      zip: customer.zip,
                      country: customer.country,
                    }}
                    selectedId={selectedPickupPointId}
                    onSelect={setSelectedPickupPointId}
                    reloadOnMove={selectedDeliveryType !== 'personalPickup'}
                  />
                </div>
              )}

              {usesZasilkovnaWidget && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="font-medium">
                    {t('delivery.zasilkovnaWidget.title')}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {selectedDeliveryMethod?.zasilkovnaApiKey
                      ? t('delivery.zasilkovnaWidget.withKey')
                      : t('delivery.zasilkovnaWidget.withoutKey')}
                  </p>
                  <a
                    className="mt-2 inline-block text-sm text-link underline-offset-4 hover:underline"
                    href="https://docs.packeta.com/guides/introduction#widget"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('delivery.zasilkovnaWidget.docs')}
                  </a>
                </div>
              )}

              {usesBalikovnaWidget && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="font-medium">
                    {t('delivery.balikovnaWidget.title')}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {t('delivery.balikovnaWidget.description')}
                  </p>
                  <a
                    className="mt-2 inline-block text-sm text-link underline-offset-4 hover:underline"
                    href="https://www.balikovna.cz/documents/20124/15302845/Bal%C3%ADkovna-widget-implementace.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('delivery.balikovnaWidget.docs')}
                  </a>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  disabled={
                    busy ||
                    !selectedDeliveryType ||
                    usesCarrierWidget ||
                    (needsPickupPoint && !selectedPickupPointId)
                  }
                  onClick={applyDelivery}
                >
                  {t('delivery.saveDelivery')}
                </Button>
                {deliveryItem?.deliveryMethodType && (
                  <StatusBadge status="paid" label={t(`deliveryTypes.${deliveryItem.deliveryMethodType}`)} />
                )}
              </div>
            </CardContent>
          </Card>

          {cartSummary}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('cart')}>
              {t('back')}
            </Button>
            <Button
              disabled={busy || !deliveryItem || !customerComplete}
              onClick={enterPayment}
            >
              {t('continue')}
            </Button>
          </div>
        </div>
      )}

      {step === 'payment' && cart && (
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('payment.title')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {paymentMethods.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('payment.noMethods')}
                </p>
              )}
              {paymentMethods.map((method) => {
                const price = method.prices.find(
                  (item) => item.price.currency === cart.currency,
                )

                return (
                  <label
                    key={method.type}
                    className="flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted has-[:checked]:border-foreground"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="paymentType"
                        checked={paymentItem?.paymentMethodType === method.type}
                        onChange={() => applyPayment(method.type)}
                      />
                      <span className="text-sm font-medium">
                        {t(`paymentTypes.${method.type}`)}
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {price ? formatMoney(price.price) : null}
                    </span>
                  </label>
                )
              })}
            </CardContent>
          </Card>

          {cartSummary}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('delivery')}>
              {t('back')}
            </Button>
            <Button
              disabled={busy || !paymentItem}
              onClick={() => setStep('review')}
            >
              {t('continue')}
            </Button>
          </div>
        </div>
      )}

      {step === 'review' && cart && (
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('review.title')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">
                  {t('review.customer')}:{' '}
                </span>
                {cart.customer.name}, {cart.customer.email}
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('review.delivery')}:{' '}
                </span>
                {deliveryItem?.deliveryMethodType
                  ? t(`deliveryTypes.${deliveryItem.deliveryMethodType}`)
                  : '-'}
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('review.payment')}:{' '}
                </span>
                {paymentItem?.paymentMethodType
                  ? t(`paymentTypes.${paymentItem.paymentMethodType}`)
                  : '-'}
              </div>
              <Separator className="my-1" />
              {cartItems.map((item) => (
                <div key={item.id} className="flex justify-between">
                  <span>
                    {item.quantity}× {item.name}
                    {item.lineExtras && item.lineExtras.length > 0 && (
                      <span className="text-muted-foreground">
                        {' '}
                        ({formatLineExtras(item)})
                      </span>
                    )}
                  </span>
                  <span>
                    {cartItemLineTotal(item)
                      ? formatMoney(cartItemLineTotal(item)!)
                      : '-'}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {cartSummary}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep('payment')}>
              {t('back')}
            </Button>
            <Button disabled={busy} onClick={promote}>
              {t('review.createOrder')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
